import {
  type FormEvent,
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { saveAs } from "file-saver";
import iso from "i18n-iso-countries";
import moment from "moment";
import { Controller, useForm, useWatch } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import usePlacesAutocomplete, {
  type Suggestion,
} from "use-places-autocomplete";

import {
  Button,
  classNames,
  Icon,
  Input,
  Select,
  Text,
  toastError,
} from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { patchDetails } from "~/v2/api/rest/account";
import { CountryCombobox, Field, MaskedDate } from "~/v2/components/form";
import type { Country } from "~/v2/types";
import { cn } from "~/v2/utils/cn";
import {
  useGetCryptoEnabled,
  useLock,
  useMutationRequest,
} from "../../../../api/hooks";
import {
  type AddressStepPayload,
  addressPatch,
  resetPatch,
} from "../../../../api/rest/onboarding";
import { parseAddress } from "../../../../utils";
import { Radio } from "../../entityOnboarding/Radio";
import { StateInput } from "../../entityOnboarding/StateInput";
import { Back } from "../back";
import {
  CUSTOMER_AGREEMENT_REVISION,
  noLastNameRequired,
  visaTypes,
} from "../constants";
import { AgreementType } from "../enums";
import { Fuzzy } from "../fuzzy";
import type { StepProps } from "../session";

// fields we are allowed to patch
const PAYLOAD_FIELDS: Array<
  // the day, month, year fields are just for the form
  keyof (AddressStepPayload & { day: string; month: string; year: string })
> = [
  "day",
  "month",
  "year",
  "last_name",
  "first_name",
  "postal_code",
  "city",
  "unit",
  "state",
  "phone_number",
  "tax_id",
  "date_of_birth",
  "tax_id_type",
  "street_address",
  "permanent_resident",
  "signup_step",
  "country_of_citizenship",
  "country_of_tax_residence",
  "margin_agreement_signed",
  "customer_agreement_signed",
  "visa_type",
  "visa_type_other_free_text",
  "visa_expiration_date",
  "country_of_birth",
];

const VISA_DATE_FORMAT = {
  input: "DD/MM/YYYY",
  output: "YYYY-MM-DD",
};

export const One = ({ session }: StepProps) => {
  const lock = useLock();

  const {
    account,
    details,
    next,
    country: {
      infos,
      allowedCountries,
      allCountries,
      alpha3ToName,
      nameToAlpha3,
    },
  } = session;

  const mutation = useMutationRequest((payload: AddressStepPayload) =>
    // switch based on v1 or v2 signups
    (account.last_onboarding_step ? addressPatch : resetPatch)(
      account.id,
      payload,
    ),
  );

  // store agreed-to terms
  const [agreements, setAgreements] = useState<AgreementType[]>([]);

  const signed = (agreement: AgreementType) => agreements.includes(agreement);

  // transform backend Date object to human friendly string
  // ex. 1998-06-03T06:00:00.000Z -> 03/06/1998
  const dateOrUndefined = useCallback(
    (date?: string | Date | null, format = "DD/MM/YYYY") =>
      date != null ? moment(date).format(format) : undefined,
    [],
  );

  const form = useForm<
    AddressStepPayload & {
      unit: string;
      same_tax_residence: string;
      day: string;
      month: string;
      year: string;
    }
  >({
    mode: "all",
    defaultValues: {
      city: details.city ?? "",
      state: details.state ?? "",
      visa_type: details.visa_type ?? undefined,
      visa_type_other_free_text: details.visa_type_other_free_text ?? undefined,
      postal_code: details.postal_code ?? "",
      tax_id_type: details.tax_id_type ?? "",
      phone_number: details.phone_number ?? "",
      // @ts-expect-error Type 'string' is not assignable to type '(string | undefined)[]'.
      street_address: details.street_address?.at(0),
      unit: details.unit ?? "",
      month: dateOrUndefined(details.date_of_birth, "M"),
      day: dateOrUndefined(details.date_of_birth, "DD"),
      year: dateOrUndefined(details.date_of_birth, "YYYY"),
      country_of_birth: alpha3ToName(details.country_of_birth ?? ""),
      visa_expiration_date: dateOrUndefined(details.visa_expiration_date),
      country_of_tax_residence: alpha3ToName(
        details.country_of_tax_residence ?? "",
      ),
      country_of_citizenship:
        details.country_of_citizenship &&
        details.country_of_citizenship !== details.country_of_tax_residence
          ? alpha3ToName(details.country_of_citizenship)
          : undefined,
      same_tax_residence:
        // country_of_tax_residence not equal to country_of_citizenship
        details.country_of_tax_residence !==
        // which defaults to country_of_tax_residence
        (details.country_of_citizenship || details.country_of_tax_residence)
          ? "no"
          : "yes",
      permanent_resident: details.permanent_resident ?? undefined,
    },
  });

  const {
    handleSubmit,
    control,
    setValue: setFormValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;

  // real-time values in the form (watched)
  const values = useWatch({ control });

  // generated payload to send in PATCH
  // biome-ignore lint/correctness/useExhaustiveDependencies: nameToAlpha3 is redefined every render and allCountries is a new array from the country hook each render, so listing them would rebuild this payload on every render. values is the real input.
  const payload = useMemo(() => {
    // create payload map
    const data = new Map<
      keyof AddressStepPayload,
      string | string[] | [string[]] | boolean | undefined | [undefined]
    >();

    PAYLOAD_FIELDS
      // of the fields that are non-null
      .filter((field) => typeof values[field] !== "undefined")
      .forEach((field) => {
        // case dependent parsing
        switch (field) {
          case "day":
          case "month":
          case "year": {
            const date = moment(
              `${values.day}/${values.month}/${values.year}`,
              "DD/MM/YYYY",
            );

            // if date is real
            date.isValid() &&
              // if within the last 100 years
              date.isAfter(moment().subtract(100, "years")) &&
              // if at least 18 years ago
              date.isBefore(moment().subtract(18, "years")) &&
              // then add it to the payload
              data.set("date_of_birth", date.format("YYYY-MM-DD"));
            break;
          }
          case "tax_id_type":
            // if tax residence is filled out
            values.country_of_tax_residence &&
              // then add it to the payload
              data.set(field, values.tax_id_type);
            break;
          case "street_address": {
            let address = values[field];
            if (typeof address === "string") {
              // @ts-expect-error Property 'trim' does not exist on type 'never''.
              address = address.trim();
            }
            data.set(field, [address]);
            break;
          }
          case "country_of_citizenship":
            data.set(
              field,
              allCountries.find(({ name: n }) => n === values[field])?.[
                "alpha-3"
              ],
            );
            break;
          case "country_of_birth":
          case "country_of_tax_residence":
            data.set(field, nameToAlpha3(values[field] || ""));
            break;
          default:
            // don't do anything special to the field
            data.set(field, values[field]);
            break;
        }
      });

    // if no country_of_citizenship
    (!data.has("country_of_citizenship") ||
      // or same_tax_residence is "yes"
      values.same_tax_residence === "yes") &&
      // set country_of_citizenship to country_of_tax_residence
      data.set("country_of_citizenship", data.get("country_of_tax_residence"));

    const sameTaxResidence =
      data.get("country_of_citizenship") ===
      data.get("country_of_tax_residence");

    // Backend requires `undefined` value if same tax residence
    if (sameTaxResidence) {
      data.delete("permanent_resident");
    }

    // Backend requires `true` value if not same tax residence and not domestic
    if (!sameTaxResidence && data.get("country_of_tax_residence") !== "USA") {
      data.set("permanent_resident", true);
    }

    // US tax residences with different citizenship will see `permanent_resident` question
    // Default is handled by useEffect below to avoid setState during render
    if (
      !sameTaxResidence &&
      data.get("country_of_tax_residence") === "USA" &&
      typeof values.permanent_resident !== "boolean"
    ) {
      data.set("permanent_resident", false);
    }

    // For users not claiming USA tax residence, USA citizens, and permanent residents
    if (
      data.get("country_of_tax_residence") !== "USA" ||
      data.get("country_of_citizenship") === "USA" ||
      data.get("permanent_resident") === true
    ) {
      // remove visa properties
      data.delete("country_of_birth");
      data.delete("visa_type");
      data.delete("visa_type_other_free_text");
      data.delete("visa_expiration_date");
    }

    // Handle visa_type_other_free_text validation
    if (data.has("visa_type")) {
      if (data.get("visa_type") === "OTHER") {
        // If visa_type is OTHER, visa_type_other_free_text is required
        const visaTypeOtherFreeText = data.get("visa_type_other_free_text");
        if (
          typeof visaTypeOtherFreeText === "string" &&
          !visaTypeOtherFreeText.trim()
        ) {
          // Remove empty description to trigger validation error
          data.delete("visa_type_other_free_text");
        }
      } else {
        // If visa_type is not OTHER, remove visa_type_other_free_text
        data.delete("visa_type_other_free_text");
      }
    }

    return Object.fromEntries(data) as AddressStepPayload;
  }, [values]);

  // Pre-sign agreements the user already signed
  // biome-ignore lint/correctness/useExhaustiveDependencies: agreements.length is deliberately narrower than agreements: this effect writes the agreements list back through setFormValue, so depending on the array identity would loop. The length is what changes when an agreement is signed.
  useEffect(() => {
    const updatedAgreements = [...agreements];
    if (
      details.customer_agreement_signed_at &&
      !agreements.includes(AgreementType.customer_agreement)
    ) {
      updatedAgreements.push(AgreementType.customer_agreement);
    }
    if (
      details.crypto_agreement_signed_at &&
      !agreements.includes(AgreementType.crypto_agreement)
    ) {
      updatedAgreements.push(AgreementType.crypto_agreement);
    }
    if (updatedAgreements.length !== agreements.length) {
      setAgreements(updatedAgreements);
    }
  }, [
    details.customer_agreement_signed_at,
    details.crypto_agreement_signed_at,
    agreements.length,
  ]);

  // Default permanent_resident to false for USA tax residents with different citizenship
  useEffect(() => {
    if (
      payload.country_of_tax_residence !== payload.country_of_citizenship &&
      payload.country_of_tax_residence === "USA" &&
      typeof values.permanent_resident !== "boolean"
    ) {
      setFormValue("permanent_resident", false);
    }
  }, [
    payload.country_of_tax_residence,
    payload.country_of_citizenship,
    setFormValue,
    values.permanent_resident,
  ]);

  const { suggestions, setValue: setPlaceValue } = usePlacesAutocomplete({
    debounce: 50,
    defaultValue: details.street_address?.at(0),
    requestOptions: {
      strictBounds: true,
      componentRestrictions: { country: "usa" },
    },
  });

  const suggested = useMemo(
    () =>
      suggestions?.data.filter(
        (value) =>
          // we only want specific types of places
          value.types.includes("street_address") ||
          value.types.includes("premise"),
      ),
    [suggestions],
  );

  // should be false until we have the country_of_tax_residence
  const crypto = useGetCryptoEnabled(
    payload.country_of_tax_residence,
    payload.state,
    infos,
  );

  // misc non-static values depending on form and payload state
  const isInternational = payload.country_of_tax_residence !== "USA";
  const isDomestic = payload.country_of_tax_residence === "USA";
  const isPermResidentRequired =
    payload.country_of_tax_residence !== payload.country_of_citizenship &&
    payload.country_of_tax_residence === "USA";
  const isVisaRequired =
    !payload.permanent_resident &&
    payload.country_of_tax_residence !== payload.country_of_citizenship &&
    payload.country_of_tax_residence === "USA";

  const hasDateOfBirthError =
    // if any of the date fields are empty
    !!errors.month ||
    !!errors.day ||
    !!errors.year ||
    // if all the date fields are filled out but the date did not parse
    (!!values.month && !!values.day && !!values.year && !payload.date_of_birth);

  // some countries don't require last name
  const isLastNameRequired = !noLastNameRequired.includes(
    payload.country_of_tax_residence || "",
  );

  const isAlpacaXCrypto = account.crypto_broker === "BS";

  const isSameTaxResidence =
    // if "yes" is selected
    values.same_tax_residence === "yes" &&
    // we use payload values because the code is parsed as alpha3
    payload.country_of_citizenship === payload.country_of_tax_residence;

  // biome-ignore lint/correctness/useExhaustiveDependencies: infos is a property of the per-render country-info object, so listing it would rebuild the tax-ID list on every render. payload.country_of_tax_residence is the real input.
  const taxIDTypes = useMemo(
    () =>
      // if payload has the country_of_tax_residence
      (
        (payload.country_of_tax_residence &&
          // we can find tax_id types for that country or default to NOT_SPECIFIED
          infos[payload.country_of_tax_residence]?.tax_id_types) || [
          {
            id_type: "NOT_SPECIFIED",
            label: "Tax Identification Number / Other",
            regexp: "^\\S{5,}$",
          },
        ]
      )
        // USA_EIN, TIN is for business onboarding
        .filter((tax_id) => !["USA_TIN", "USA_EIN"].includes(tax_id.id_type)),
    [payload.country_of_tax_residence],
  );

  // there will always be at least one tax_id_type
  const taxIDDefaultType = taxIDTypes[0];
  const hasSingleTaxIDType = taxIDTypes.length === 1;

  // the max-length of tax_id we will enforce
  const taxIDMaxLength = isDomestic ? 11 : undefined;

  // the regex used to validate the tax_id
  const taxIDRegex = new RegExp(
    taxIDTypes.find(
      (tax_id) =>
        tax_id.id_type === (payload.tax_id_type || taxIDDefaultType.id_type),
    )?.regexp || taxIDDefaultType.regexp,
  );

  useEffect(() => {
    if (
      // if country_of_tax_residence has been set
      payload.country_of_tax_residence &&
      // and the tax_id_type does not exist in the tax_id_types
      !taxIDTypes
        .map(({ id_type }) => id_type)
        .includes(values.tax_id_type || "")
    ) {
      // we need to update it to the default
      setFormValue("tax_id_type", taxIDDefaultType.id_type);
      setFormValue("tax_id", "");
      clearErrors("tax_id");
    }
  }, [
    payload.country_of_tax_residence,
    setFormValue,
    clearErrors,
    values.tax_id_type,
    taxIDDefaultType.id_type,
    taxIDTypes,
  ]);

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    // we are only masking domestic SSN here
    if (isInternational) {
      return;
    }

    const value = event.currentTarget.value;

    // handle user input of hyphen
    if (value.length === 3 || value.length === 6) {
      if (event.key !== "-" && /[0-9]/.test(String.fromCharCode(event.which))) {
        event.currentTarget.value += "-";
      }
    }
  };

  const onInput = (event: FormEvent<HTMLInputElement>) =>
    // enforce alphanumeric input with regex
    (event.currentTarget.value = event.currentTarget.value.replace(
      /[^0-9]/g,
      "",
    ));

  // handle form submission, error toast is built-in to mutation
  const onSubmit = () => {
    if (lock.locked) {
      return;
    }

    if (!signed(AgreementType.customer_agreement)) {
      toastError("Please sign customer agreement");
      return;
    }

    lock.lock();

    // they can't get here without signing
    payload.customer_agreement_signed = true;
    payload.customer_agreement_revision = CUSTOMER_AGREEMENT_REVISION;

    // Reformat visa expiration date to output format for backend
    if (payload.visa_expiration_date) {
      payload.visa_expiration_date = moment(
        payload.visa_expiration_date,
        VISA_DATE_FORMAT.input,
      ).format(VISA_DATE_FORMAT.output);
    }

    // Ensure state is capitalized, backend doesn't validate but is required
    if (isDomestic) {
      payload.state = payload.state?.toLocaleUpperCase();
    }

    // send the payload
    mutation.mutate(payload, {
      onSuccess: async () => {
        // if crypto is enabled
        crypto &&
          // send patch to sign the agreement
          (await patchDetails(account.id, {
            crypto_agreement_signed_at: new Date(),
            crypto_agreement_revision: "02.17.2023",
          }).catch(console.error));

        // go to next step
        next();

        // call amplitude event
        AmplitudeProvider.dispatch("onboardingv2_step_one_complete");

        // allow re-submission
        lock.unlock();
      },
      onError: () => lock.unlock(),
    });
  };

  const onSign = (agreement: AgreementType, openTab = true) => {
    // if agreement does not have a value
    if (!agreement) {
      console.error("No pdf found for agreement", agreement);
      return;
    }

    // otherwise, add it to the agreements array
    if (!agreements.includes(agreement)) {
      setAgreements([...agreements, agreement]);
    }

    if (agreement === AgreementType.crypto_agreement && isAlpacaXCrypto) {
      agreement = AgreementType.alpacax_crypto_agreement;
    }

    openTab &&
      // open the pdf in a new tab
      window.open(agreement, "_blank");
  };

  const onDidSelectCountry = (country: Country | null) => {
    const taxResidence = country?.name ?? "";
    setFormValue("country_of_tax_residence", taxResidence);

    if (values.country_of_citizenship === taxResidence) {
      setFormValue("country_of_citizenship", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    setFormValue("street_address", [""]);
    setFormValue("city", "");
    setFormValue("unit", "");
    setFormValue("state", "");
    setFormValue("postal_code", "");

    for (const name of [
      "street_address",
      "city",
      "unit",
      "state",
      "postal_code",
    ]) {
      // this is a hack to get around same-name fields
      document.getElementsByName(name).forEach((elem) => {
        (elem as HTMLInputElement).value = "";
      });
    }
  };

  const onDidSelectStreetAddress = async ({ description }: Suggestion) => {
    try {
      const address = await parseAddress(description);

      setFormValue("city", address.city);
      setFormValue("state", address.state);
      setFormValue("postal_code", address.postal_code);
    } catch (err) {
      console.error(err);
    }
  };

  const [debug, setDebug] = useState<string>();

  useEffect(() => {
    setDebug(JSON.stringify(payload, null, "  "));
  }, [payload]);

  return (
    <div>
      <h2 className="mb-8 text-lg">{session.step.title}</h2>
      {[""].includes(window.env.ENV) && (
        <div className="mb-4">
          <pre
            style={{
              fontSize: "13px",
              borderRadius: "6px",
              borderColor: "#bfdbfe",
              background: "#dbeafe",
              color: "#18181b",
            }}
          >
            {debug}
          </pre>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col items-start gap-4">
          <div className="flex w-full">
            <div className="mt-0 mr-4 w-full grow">
              <Field
                label="First Name"
                required={true}
                error={!!errors.first_name}
                errorMsg="Required"
              >
                <Controller
                  name="first_name"
                  defaultValue={
                    details.given_name || details.legal_name?.split(" ")?.at(0)
                  }
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input placeholder="John" {...field} />
                  )}
                />
              </Field>
            </div>
            <div className="w-full grow">
              <Field
                label="Last Name"
                required={isLastNameRequired}
                error={!!errors.last_name}
                errorMsg="Required"
              >
                <Controller
                  name="last_name"
                  defaultValue={
                    details.family_name || details.legal_name?.split(" ")?.at(1)
                  }
                  control={control}
                  rules={{ required: isLastNameRequired }}
                  render={({ field }) => <Input placeholder="Doe" {...field} />}
                />
              </Field>
            </div>
          </div>
          <div className="flex w-full items-end justify-between">
            <div className="mr-4 grow">
              <Field
                label="Date of Birth"
                error={hasDateOfBirthError}
                required={true}
              >
                <Controller
                  name="month"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      placeholder="Month"
                      options={[
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ].map((month, i) => ({
                        label: month,
                        value: `${i + 1}`,
                      }))}
                      multiple={false}
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
            <div className="mr-4 grow">
              <Field label="" error={hasDateOfBirthError}>
                <Controller
                  name="day"
                  control={control}
                  rules={{
                    required: true,
                    validate: (v) => {
                      const next = Number(v);
                      return !(next > 31 || next < 1);
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      maxLength={2}
                      onInput={onInput}
                      placeholder="Day"
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
            <div className="grow">
              <Field
                label=""
                error={hasDateOfBirthError}
                errorMsg={errors.year ? "Must be 18+" : "Invalid date"}
              >
                <Controller
                  name="year"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      placeholder="Year"
                      maxLength={4}
                      onInput={onInput}
                      {...field}
                    />
                  )}
                />
              </Field>
            </div>
          </div>
          <div>
            <Field
              label="Phone Number"
              required={true}
              error={!!errors.phone_number}
              errorMsg="Required"
            >
              <Controller
                control={control}
                name="phone_number"
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className={cn(errors.phone_number && "phone-err")}>
                    <PhoneInput
                      containerClass={classNames(
                        error && "rounded-lg border border-red-400",
                      )}
                      inputClass="bg-white rounded-lg text-sm border border-gray-200 text-gray-900 placeholder-gray-500"
                      placeholder="+1 (123) 456-7890"
                      onChange={onChange}
                      value={value}
                      country={iso
                        .alpha3ToAlpha2(
                          // this is the only country we will have at first
                          details.country_of_tax_residence || "USA",
                        )
                        ?.toLowerCase()}
                    />
                  </div>
                )}
              />
            </Field>
          </div>
          <div className="w-full">
            <Field
              required={true}
              label="Country of Tax Residence"
              error={!!errors.country_of_tax_residence}
              errorMsg={
                errors?.country_of_tax_residence?.type === "required"
                  ? "Required"
                  : "Select from the dropdown"
              }
            >
              <Controller
                name="country_of_tax_residence"
                control={control}
                rules={{
                  required: true,
                  validate: (value) =>
                    allowedCountries.find(({ name }) => name === value) !==
                    undefined,
                }}
                render={({ field }) => (
                  <CountryCombobox
                    name={field.name}
                    placeholder="United States of America"
                    items={allowedCountries}
                    value={field.value}
                    onChange={field.onChange}
                    onDidSelect={onDidSelectCountry}
                  />
                )}
              />
            </Field>
          </div>
          <div>
            <Field
              label="Is your country of citizenship the same as your country of tax
              residence?"
            >
              <Controller
                name="same_tax_residence"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="mt-1 flex flex-col gap-1">
                    <Radio
                      label="Yes"
                      name="same_tax_residence_yes"
                      isChecked={field.value === "yes"}
                      onChange={() => field.onChange("yes")}
                    />
                    <Radio
                      label="No"
                      name="same_tax_residence_no"
                      isChecked={field.value === "no"}
                      onChange={() => field.onChange("no")}
                    />
                  </div>
                )}
              />
            </Field>
          </div>
          {!isSameTaxResidence && (
            <>
              <div className="flex w-full justify-between">
                <div className="mr-4 grow">
                  <Field
                    label="Country of Citizenship"
                    required={!isSameTaxResidence}
                    error={!!errors.country_of_citizenship}
                    errorMsg={
                      errors?.country_of_citizenship?.type === "required"
                        ? "Required"
                        : "Select from the dropdown"
                    }
                  >
                    <Controller
                      name="country_of_citizenship"
                      control={control}
                      rules={{
                        required: !isSameTaxResidence,
                        validate: (value) =>
                          value !== values.country_of_tax_residence &&
                          allCountries.some(({ name }) => name === value),
                      }}
                      render={({ field }) => (
                        <CountryCombobox
                          name={field.name}
                          placeholder="United States of America"
                          items={allCountries.filter(
                            (country) =>
                              country["alpha-3"] !==
                              payload.country_of_tax_residence,
                          )}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Field>
                </div>
              </div>
              {isPermResidentRequired && (
                <div>
                  <Field
                    label="Are you a US Permanent Resident?"
                    required={isPermResidentRequired}
                    errorMsg="Required"
                  >
                    <Controller
                      name="permanent_resident"
                      control={control}
                      render={({ field }) => (
                        <div className="mt-2 flex flex-col gap-1">
                          <Radio
                            label="Yes"
                            name="permanent_resident_yes"
                            isChecked={field.value === true}
                            onChange={() => field.onChange(true)}
                          />
                          <Radio
                            label="No"
                            name="permanent_resident_no"
                            isChecked={field.value === false}
                            onChange={() => field.onChange(false)}
                          />
                        </div>
                      )}
                    />
                  </Field>
                </div>
              )}
              {isVisaRequired && (
                <>
                  <div className="w-full">
                    <Field
                      label="Country of Birth"
                      required={isVisaRequired}
                      error={!!errors.country_of_birth}
                      errorMsg={
                        errors?.country_of_birth?.type === "required"
                          ? "Required"
                          : "Select from the dropdown"
                      }
                    >
                      <Controller
                        name="country_of_birth"
                        control={control}
                        rules={{
                          required: isVisaRequired,
                          validate: (value) =>
                            allCountries.find(({ name }) => name === value) !==
                            undefined,
                        }}
                        render={({ field }) => (
                          <CountryCombobox
                            name={field.name}
                            placeholder="United States of America"
                            items={allCountries}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </Field>
                  </div>
                  <div className="flex w-full justify-between">
                    <div className="mr-4 w-full grow">
                      <Field label="Visa Type" required={!!isVisaRequired}>
                        <Controller
                          name="visa_type"
                          control={control}
                          rules={{ required: isVisaRequired }}
                          render={({ field }) => (
                            <Select
                              placeholder="Select a type..."
                              options={visaTypes.map(({ text, value }) => ({
                                label: text,
                                value,
                              }))}
                              {...field}
                            />
                          )}
                        />
                      </Field>
                    </div>
                    <div className="w-full grow">
                      <Field
                        label="Visa Expiration Date"
                        required={isVisaRequired}
                        error={!!errors.visa_expiration_date}
                        errorMsg={
                          errors.visa_expiration_date?.type === "required"
                            ? "Required"
                            : "Invalid date"
                        }
                      >
                        <Controller
                          name="visa_expiration_date"
                          control={control}
                          render={({ field }) => <MaskedDate {...field} />}
                          rules={{
                            required: isVisaRequired,
                            maxLength: 10,
                            validate: (value) => {
                              const date = moment(
                                value,
                                VISA_DATE_FORMAT.input,
                                true,
                              );
                              return date.isValid() && date.isAfter(moment());
                            },
                          }}
                        />
                      </Field>
                    </div>
                  </div>
                  {values.visa_type === "OTHER" && (
                    <div className="w-full">
                      <Field
                        label="Visa Type Description"
                        required={
                          isVisaRequired && values.visa_type === "OTHER"
                        }
                        error={!!errors.visa_type_other_free_text}
                        errorMsg={
                          errors.visa_type_other_free_text?.type === "required"
                            ? "Required"
                            : "Please provide a description"
                        }
                      >
                        <Controller
                          name="visa_type_other_free_text"
                          control={control}
                          rules={{
                            required:
                              isVisaRequired && values.visa_type === "OTHER",
                            validate: (value) => {
                              if (values.visa_type === "OTHER") {
                                return value?.trim()
                                  ? true
                                  : "Description cannot be empty";
                              }
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              placeholder="e.g., A-1 Diplomatic Visa"
                              maxLength={50}
                              {...field}
                            />
                          )}
                        />
                      </Field>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          <div className="w-full">
            <div className="flex w-full justify-between">
              <div className="mr-4 grow">
                <Field
                  label="Street Address"
                  required={true}
                  error={!!errors.street_address}
                  errorMsg="Required"
                >
                  {isInternational ? (
                    <Controller
                      control={control}
                      name="street_address"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          autoComplete="address-line1"
                          placeholder="1234 Alpaca St"
                          {...field}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name="street_address"
                      control={control}
                      rules={{
                        required: true,
                        onChange: async ({ target }) =>
                          setPlaceValue(target.value),
                      }}
                      render={({ field }) => (
                        <Fuzzy
                          {...field}
                          hook={form}
                          items={suggested}
                          strict={false}
                          options={{ threshold: 0.5 }}
                          placeholder="1234 Alpaca St"
                          autoComplete="address-line1"
                          accessors={{
                            text: "description",
                            value: "structured_formatting.main_text",
                            search: [
                              "description",
                              "structured_formatting.main_text",
                            ],
                          }}
                          onDidSelect={onDidSelectStreetAddress}
                        />
                      )}
                    />
                  )}
                </Field>
              </div>
              <div className="w-full max-w-[25%]">
                <Field label="Unit">
                  <Controller
                    control={control}
                    name="unit"
                    render={({ field }) => (
                      <Input
                        autoComplete="address-line2"
                        maxLength={20}
                        placeholder="1234"
                        {...field}
                      />
                    )}
                  />
                </Field>
              </div>
            </div>
            <div className="mt-4 flex justify-between">
              <div className="mr-4">
                <Field
                  label="City"
                  required={true}
                  error={!!errors.city}
                  errorMsg="Required"
                >
                  <Controller
                    name="city"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Input
                        autoComplete="address-level2"
                        placeholder="San Mateo"
                        {...field}
                      />
                    )}
                  />
                </Field>
              </div>
              <div className="mr-4 grow">
                {isDomestic ? (
                  <Field
                    label="State"
                    required
                    error={!!errors.state}
                    errorMsg="Required"
                  >
                    <StateInput name="state" control={control} isRequired />
                  </Field>
                ) : (
                  <Field
                    label="State / Province"
                    required={false}
                    error={false}
                  >
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <Input
                          autoComplete="address-level1"
                          maxLength={36}
                          placeholder="State / Province"
                          {...field}
                        />
                      )}
                    />
                  </Field>
                )}
              </div>
              <div className="w-full max-w-[30%]">
                <Field
                  label="Postal Code"
                  required={true}
                  error={!!errors.postal_code}
                  errorMsg="Required"
                >
                  <Controller
                    name="postal_code"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Input
                        autoComplete="postal-code"
                        placeholder="94401"
                        {...field}
                      />
                    )}
                  />
                </Field>
              </div>
            </div>
          </div>
          <div>
            <Field label="Tax ID Type" required={true}>
              <Controller
                name="tax_id_type"
                control={control}
                rules={{
                  required: true,
                  onChange: () => {
                    setFormValue("tax_id", "");
                    clearErrors("tax_id");
                  },
                }}
                render={({ field: { ref, ...field } }) =>
                  hasSingleTaxIDType ? (
                    <div className="flex h-10 items-center rounded border border-gray-200 bg-gray-50 px-3 text-gray-700 text-sm">
                      {taxIDDefaultType.label}
                    </div>
                  ) : (
                    <Select
                      defaultValue={taxIDDefaultType.id_type}
                      options={taxIDTypes.map(({ label, id_type }) => ({
                        label,
                        value: id_type,
                      }))}
                      {...field}
                    />
                  )
                }
              />
            </Field>
          </div>
          <div className="mt-auto">
            <Field
              label="Tax ID"
              required={true}
              error={!!errors.tax_id}
              errorMsg={
                errors.tax_id?.type === "pattern"
                  ? "Invalid tax ID format"
                  : "Required"
              }
            >
              <Controller
                name="tax_id"
                control={control}
                rules={{ required: true, pattern: taxIDRegex }}
                render={({ field }) => (
                  <Input
                    placeholder={"123-45-6789"}
                    onKeyDown={onKeyDown}
                    maxLength={taxIDMaxLength}
                    {...field}
                  />
                )}
              />
            </Field>
          </div>
          <div className="mt-5 w-full">
            <Field
              label="Please read and agree to the following"
              required={true}
            >
              <div className="mt-4">
                <div className="flex w-full items-center justify-between">
                  <div
                    className={classNames(
                      "flex items-center justify-center py-[0.18rem] pr-[0.3rem] pl-[0.6rem] text-[0.96rem]",
                      signed(AgreementType.customer_agreement)
                        ? "bg-[rgba(0,201,167,0.1)] text-[rgb(0,201,167)]"
                        : "bg-[rgba(237,76,120,0.1)] text-[rgb(237,76,120)]",
                    )}
                  >
                    {signed(AgreementType.customer_agreement) ? (
                      <Icon
                        name="Check"
                        className="h-4 w-4 text-[rgb(0,201,167)]"
                      />
                    ) : (
                      <Icon name="DocumentText" className="h-4 w-4" />
                    )}
                    <Text className="max-w-[64px] truncate text-inherit min-[440px]:max-w-fit">
                      &nbsp;&nbsp;customer_agreement.pdf&nbsp;&nbsp;&nbsp;
                    </Text>
                  </div>
                  <div className="flex items-center justify-center">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        // download the agreement
                        saveAs(
                          AgreementType.customer_agreement,
                          "customer_agreement.pdf",
                        );

                        // sign it
                        onSign(AgreementType.customer_agreement, false);
                      }}
                    >
                      Download&nbsp;
                      <Icon className="h-4 w-4" name="ArrowDownTray" />
                    </Button>
                    &nbsp;
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => onSign(AgreementType.customer_agreement)}
                    >
                      View&nbsp;
                      <Icon className="h-4 w-4" name="ArrowTopRightOnSquare" />
                    </Button>
                  </div>
                </div>
                {crypto && (
                  <div className="mt-1 flex w-full items-center justify-between">
                    <div
                      className={classNames(
                        "flex items-center justify-center py-[0.18rem] pr-[0.3rem] pl-[0.6rem] text-[0.96rem]",
                        signed(AgreementType.crypto_agreement)
                          ? "bg-[rgba(0,201,167,0.1)] text-[rgb(0,201,167)]"
                          : "bg-[rgba(237,76,120,0.1)] text-[rgb(237,76,120)]",
                      )}
                    >
                      {signed(AgreementType.crypto_agreement) ? (
                        <Icon
                          name="Check"
                          className="h-4 w-4 text-[rgb(0,201,167)]"
                        />
                      ) : (
                        <Icon name="DocumentText" className="h-4 w-4" />
                      )}
                      <Text className="max-w-[64px] truncate text-inherit min-[440px]:max-w-fit">
                        &nbsp;&nbsp;crypto_agreement.pdf&nbsp;&nbsp;&nbsp;
                      </Text>
                    </div>
                    <div className="flex items-center justify-center">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          // download the agreement
                          saveAs(
                            isAlpacaXCrypto
                              ? AgreementType.alpacax_crypto_agreement
                              : AgreementType.crypto_agreement,
                            "crypto_agreement.pdf",
                          );

                          // sign it
                          onSign(AgreementType.crypto_agreement, false);
                        }}
                      >
                        Download&nbsp;
                        <Icon className="h-4 w-4" name="ArrowDownTray" />
                      </Button>
                      &nbsp;
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onSign(AgreementType.crypto_agreement)}
                      >
                        View&nbsp;
                        <Icon
                          className="h-4 w-4"
                          name="ArrowTopRightOnSquare"
                        />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Field>
          </div>
        </div>
        <Text className="my-8 text-gray-600 text-sm">
          By clicking the button below you hereby agree to be bound to the terms
          of the Application Agreement as provided to you. Your electronic
          signature will have the same effect as physically signing and
          returning the Application Agreement.
        </Text>
        <div className="flex items-center justify-end">
          <Back
            session={session}
            patch={false}
            onBeforeBack={() =>
              window.localStorage?.removeItem("account_type_step")
            }
          />
          <Button className="ml-1" type="submit" disabled={isSubmitting}>
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default One;
